/**
 * Debug Logger - Conditional Console Logging
 *
 * Purpose: Enable debug logging in development, disable in production
 * Created: November 1, 2025
 * Status: Production Ready
 *
 * Usage:
 * window.debugLogger.log('Cart opened');
 * window.debugLogger.warn('Deprecated method');
 * window.debugLogger.error('Critical error'); // Always logs
 */

// Guard to prevent duplicate initialization
if (typeof window.DebugLogger === 'undefined') {

class DebugLogger {
  constructor() {
    // Enable debug mode in:
    // - localhost
    // - Shopify preview (*.myshopify.com)
    // - Manual debug flag (?debug=true)
    this.enabled = window.location.hostname === 'localhost' ||
                   window.location.hostname === '0.0.0.0' ||
                   window.location.hostname.includes('127.0.0.1') ||
                   window.location.hostname.includes('.myshopify.com') ||
                   window.location.search.includes('debug=true');

    // Log initialization status
    if (this.enabled) {
      console.log('[DebugLogger] Debug mode enabled');
      console.log('[DebugLogger] Environment:', window.location.hostname);
    }
  }

  /**
   * Log debug message (only in development)
   * @param {string} message - Message to log
   * @param {...any} args - Additional arguments
   */
  log(message, ...args) {
    if (this.enabled) {
      console.log(`[DEBUG] ${message}`, ...args);
    }
  }

  /**
   * Log warning (only in development)
   * @param {string} message - Warning message
   * @param {...any} args - Additional arguments
   */
  warn(message, ...args) {
    if (this.enabled) {
      console.warn(`[WARN] ${message}`, ...args);
    }
  }

  /**
   * Log error (ALWAYS logs - even in production)
   * @param {string} message - Error message
   * @param {...any} args - Additional arguments
   */
  error(message, ...args) {
    // Always log errors, even in production
    console.error(`[ERROR] ${message}`, ...args);
  }

  /**
   * Create console group (only in development)
   * @param {string} label - Group label
   */
  group(label) {
    if (this.enabled) {
      console.group(label);
    }
  }

  /**
   * End console group (only in development)
   */
  groupEnd() {
    if (this.enabled) {
      console.groupEnd();
    }
  }

  /**
   * Log table (only in development)
   * @param {*} data - Data to display in table
   */
  table(data) {
    if (this.enabled) {
      console.table(data);
    }
  }

  /**
   * Time operation (only in development)
   * @param {string} label - Timer label
   */
  time(label) {
    if (this.enabled) {
      console.time(label);
    }
  }

  /**
   * End time operation (only in development)
   * @param {string} label - Timer label
   */
  timeEnd(label) {
    if (this.enabled) {
      console.timeEnd(label);
    }
  }

  /**
   * Check if debug mode is enabled
   * @returns {boolean} True if debug mode enabled
   */
  isEnabled() {
    return this.enabled;
  }
}

// Create global instance
window.DebugLogger = DebugLogger;
window.debugLogger = new DebugLogger();

} // End guard
